import { CheckCircle2, Clock, Ban, Banknote, FileCheck2, AlertCircle, FileText, Send, Building2, CloudDownload } from 'lucide-react';

export const TIPO_LABEL = {
  '33': 'Factura Afecta',
  '34': 'Factura Exenta',
  '52': 'Guía de Despacho',
  '61': 'Nota de Crédito',
  '56': 'Nota de Débito',
};

// Color por tipo de documento (etiquetas, chips de filtro y tarjetas).
export const TIPO_COLOR = {
  '33': { text: 'text-emerald-700', bg: 'bg-emerald-100', border: 'border-emerald-300', dot: 'bg-emerald-500', active: 'bg-emerald-600 text-white border-emerald-600' },
  '34': { text: 'text-yellow-700', bg: 'bg-yellow-100', border: 'border-yellow-300', dot: 'bg-yellow-500', active: 'bg-yellow-500 text-white border-yellow-500' },
  '61': { text: 'text-red-700', bg: 'bg-red-100', border: 'border-red-300', dot: 'bg-red-500', active: 'bg-red-600 text-white border-red-600' },
  '56': { text: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300', dot: 'bg-orange-500', active: 'bg-orange-600 text-white border-orange-600' },
  '52': { text: 'text-blue-700', bg: 'bg-blue-100', border: 'border-blue-300', dot: 'bg-blue-500', active: 'bg-blue-600 text-white border-blue-600' },
};

export function TipoBadge({ tipo }) {
  const c = TIPO_COLOR[String(tipo)] || { text: 'text-gray-700', bg: 'bg-gray-100', border: 'border-gray-300' };
  return (
    <span data-testid={`tipo-badge-${tipo}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold whitespace-nowrap ${c.bg} ${c.text} ${c.border}`}>
      {TIPO_LABEL[String(tipo)] || tipo}
    </span>
  );
}

const ESTADO_STYLE = {
  borrador: 'bg-gray-100 text-gray-700 border-gray-200',
  pendiente_emision: 'bg-amber-100 text-amber-800 border-amber-200',
  enviado_dtecore: 'bg-blue-100 text-blue-800 border-blue-200',
  procesado_dtecore: 'bg-blue-100 text-blue-800 border-blue-200',
  enviado_sii: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  aceptado_sii: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rechazado_sii: 'bg-red-100 text-red-800 border-red-200',
  enviado_receptor: 'bg-teal-100 text-teal-800 border-teal-200',
  pendiente_contabilizacion: 'bg-amber-100 text-amber-800 border-amber-200',
  contabilizado: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  error_contabilizacion: 'bg-red-100 text-red-800 border-red-200',
  cedido_factoring: 'bg-purple-100 text-purple-800 border-purple-200',
  anulado_nc: 'bg-gray-200 text-gray-500 border-gray-300 line-through',
};

export function EstadoBadge({ estado, label }) {
  const cls = ESTADO_STYLE[estado] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span data-testid={`estado-badge-${estado}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${cls}`}>
      {label || estado}
    </span>
  );
}

const SITUACION = {
  borrador: { label: 'Borrador', cls: 'bg-gray-100 text-gray-600 border-gray-200', icon: FileText },
  vigente: { label: 'Vigente', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  cobrada: { label: 'Cobrada', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: Banknote },
  cedida: { label: 'Cedida (factoring)', cls: 'bg-purple-100 text-purple-700 border-purple-200', icon: Send },
  anulada: { label: 'Anulada', cls: 'bg-red-100 text-red-700 border-red-200', icon: Ban },
};

export function SituacionBadge({ situacion }) {
  const s = SITUACION[situacion] || SITUACION.borrador;
  const Icon = s.icon;
  return (
    <span data-testid={`situacion-badge-${situacion}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${s.cls}`}>
      <Icon className="w-3 h-3" />{s.label}
    </span>
  );
}

const CONTABLE = {
  pendiente: { label: 'Pend. contabilizar', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  pendiente_sii: { label: 'Esperando SII', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Clock },
  rechazado_sii: { label: 'Rechazado SII', cls: 'bg-red-100 text-red-700 border-red-200', icon: Ban },
  pendiente_proforma: { label: 'Falta proforma', cls: 'bg-orange-100 text-orange-800 border-orange-200', icon: AlertCircle },
  en_proceso: { label: 'Contabilizando…', cls: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  contabilizado: { label: 'Contabilizado', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: FileCheck2 },
  error_contabilizacion: { label: 'Error contable', cls: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle },
  error: { label: 'Error contable', cls: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle },
};

export function EstadoContableBadge({ estado }) {
  const s = CONTABLE[estado] || CONTABLE.pendiente;
  const Icon = s.icon;
  return (
    <span data-testid={`contable-badge-${estado}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${s.cls}`}>
      <Icon className="w-3 h-3" />{s.label}
    </span>
  );
}

const FACTORING = {
  no_cedible: { label: 'No cedible', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  disponible: { label: 'Cedible disponible', cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  en_evaluacion: { label: 'En evaluación', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  cedida: { label: 'Cedida', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  financiada: { label: 'Financiada', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  liquidada: { label: 'Liquidada', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

export function FactoringBadge({ factoring }) {
  const estado = (factoring || {}).estado || 'no_cedible';
  const s = FACTORING[estado] || FACTORING.no_cedible;
  return (
    <span data-testid={`factoring-badge-${estado}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${s.cls}`}>
      <Banknote className="w-3 h-3" />{s.label}
    </span>
  );
}

const ORIGEN = {
  erp: { label: 'ERP', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Building2 },
  externo: { label: 'Externo', cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: CloudDownload },
};

export function OrigenBadge({ origen }) {
  const s = ORIGEN[origen === 'externo' ? 'externo' : 'erp'];
  const Icon = s.icon;
  return (
    <span data-testid={`origen-badge-${origen === 'externo' ? 'externo' : 'erp'}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${s.cls}`}>
      <Icon className="w-3 h-3" />{s.label}
    </span>
  );
}
